import { useQuery } from '@tanstack/react-query';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@nesso/ui/primitives/card';
import { Separator } from '@nesso/ui/primitives/separator';

import { formatPlanTerms, paidPlansOptions } from '@/entities/subscription';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatOrdinalDay } from '@/shared/lib/time-utils';

type ResubscribeSheetSummaryProps = {
    planCode: string;
};

export function ResubscribeSheetSummary({ planCode }: ResubscribeSheetSummaryProps) {
    const { data: plans } = useQuery(paidPlansOptions());

    const plan = plans?.find((p) => p.code === planCode);
    const planTerms = plan ? formatPlanTerms(plan) : null;
    const billingDay = formatOrdinalDay(new Date());
    const total = plan
        ? formatCurrencyFromCents(plan.basePriceCents, 'USD', { alwaysShowCents: true })
        : '—';

    return (
        <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-1">
                <p className="text-lg font-medium">New plan</p>
                <p className="text-base text-muted-foreground">Bills monthly on the {billingDay}</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{plan?.name ?? 'Selected plan'}</CardTitle>
                    {planTerms && <CardDescription>{planTerms}</CardDescription>}
                </CardHeader>
                <CardContent className="flex items-center gap-3 pt-4">
                    <span className="text-3xl font-semibold">
                        {plan ? formatCurrencyFromCents(plan.basePriceCents) : '—'}
                    </span>
                    <span className="text-sm leading-none text-muted-foreground">
                        per
                        <br />
                        month
                    </span>
                </CardContent>
            </Card>

            <div className="flex flex-col gap-4">
                <p className="text-lg font-semibold">Upcoming payment</p>
                <div className="flex items-center justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{total}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between text-sm">
                    <span>Total</span>
                    <span>{total}</span>
                </div>
            </div>
        </div>
    );
}
